import { createClient } from '@supabase/supabase-js';
import type { Database } from '../types/database';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate environment variables
if (!supabaseUrl || !supabaseAnonKey) throw new Error('Missing Supabase environment variables');

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    // Add token refresh handling
    onAuthStateChange: (event, session) => {
      console.log('Auth state changed:', event);
      
      // If token is refreshed, update any pending requests
      if (event === 'TOKEN_REFRESHED') {
        console.log('Token refreshed successfully');
      }
      
      // If signed out, clear any pending operations
      if (event === 'SIGNED_OUT') {
        console.log('User signed out');
      }
    },
  },
  global: {
    headers: {
      'X-Client-Info': 'tezhly-platform'
    }
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  },
  db: {
    schema: 'public'
  }
});

// Add connection status check
async function checkConnection(): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('count')
      .limit(1)
      .single();
    return !error;
  } catch {
    return false;
  }
}

// Add connection recovery function
async function recoverConnection(retryCount = 0, maxRetries = 3): Promise<boolean> {
  if (retryCount >= maxRetries) {
    return false;
  }
  try {
    const isConnected = await checkConnection();
    if (isConnected) {
      return true;
    }
    // Exponential backoff
    await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000));
    return recoverConnection(retryCount + 1, maxRetries);
  } catch {
    return false;
  }
}

// Enhanced error handler with refresh token handling
function handleSupabaseError(error: unknown): void {
  if (error instanceof Error) {
    const errorMessage = error.message.toLowerCase();
    
    // Handle auth token errors
    if (
      errorMessage.includes('invalid refresh token') || 
      errorMessage.includes('refresh token not found') ||
      errorMessage.includes('jwt expired')
    ) {
      console.warn('Auth token error, attempting to recover session');
      
      // Try to refresh the session
      supabase.auth.refreshSession().catch(() => {
        // If refresh fails, redirect to login
        console.warn('Session refresh failed, redirecting to login');
        supabase.auth.signOut();
        window.location.href = '/login'; // Adjust this path to your login page
      });
      
      return;
    }
    
    // Handle network errors
    if (errorMessage.includes('failed to fetch') || errorMessage.includes('network request failed')) {
      recoverConnection();
    }
  }
}

// Add a session recovery function
async function recoverSession(): Promise<boolean> {
  try {
    const { data } = await supabase.auth.getSession();
    
    // No session found
    if (!data.session) {
      return false;
    }
    
    // Test if session is valid with a simple request
    const { error } = await supabase.from('user_profiles').select('count').limit(1);
    
    // If we get an auth error, try to refresh
    if (error && (
      error.message.includes('JWT expired') || 
      error.message.includes('Invalid Refresh Token')
    )) {
      const { data } = await supabase.auth.refreshSession();
      return !!data.session;
    }
    
    return true;
  } catch (error) {
    console.error('Error recovering session:', error);
    return false;
  }
}